import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Select, TextField } from 'mui-rff';
import {
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Search as SearchIcon,
  ReplayCircleFilled as ReplayCircleFilledIcon,
} from '@mui/icons-material';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import ConfirmationDialog from '@components/ConfirmationDialog';
import MultiSelect from '@components/MultiSelect';
import { Typography } from '@components/styledComponents';
import { showErrorMessage, showSuccessMessage } from '@store/dashboard';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { viewLPRates } from '../conversion-rates.breadcrumbs';

import {
  getLoyaltyPointsRates,
  loyaltyActions,
  selectIsFetchingLPRates,
  selectIsUpdatingLPRates,
  selectLoyaltyPointsRates,
  updateLPRates,
} from '../store/loyalty-points.slice';
import { getMCCCurrencies, selectMCCCurrencies } from '../store/mcc.slice';
import {
  getLOBs,
  selectIsFetchingLOBList,
  selectLOBList,
} from '../../payment-options/store/payment-options-merchants';

import ApplicationPageTemplate from '../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import {
  LP_COLUMNS,
  LP_LEFT_COLUMN_STYLE,
} from '../conversion-rates.constants';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

function SearchForm({
  currencies,
  handleSubmit,
  handleLOBChange,
  formErrorMsg,
  invalid,
  isFetchingCurrencies,
  isFetchingLOBs,
  isFetchingRates,
  lobs,
  setFormErrorMsg,
  values,
}) {
  return (
    <form
      onSubmit={handleSubmit}
      noValidate>
      <Grid
        container
        spacing={2}
        alignItems="center">
        <Grid
          item
          xs={8}
          lg={3}>
          <MultiSelect
            id="lob"
            name="lob"
            label="Select LOB"
            required
            items={lobs}
            size="small"
            variant="outlined"
            readOnly={isFetchingLOBs}
            value={values.lob}
            isOptionEqualToValue={(option, value) =>
              option.lobId === value.lobId
            }
            multiple={false}
            optionDisplayKey="lobCode"
            onChange={handleLOBChange}
          />
        </Grid>
        <Grid
          item
          xs={4}
          lg={2}>
          {currencies && currencies.length > 0 && (
            <Select
              name="currency"
              size="small"
              variant="outlined"
              required
              disabled={isFetchingCurrencies}
              label="Currency">
              {currencies.map((currency) => (
                <MenuItem
                  key={currency}
                  value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </Select>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          lg={4}>
          <TextField
            size="small"
            name="snapshotId"
            label="Conversion Snapshot ID"
            variant="outlined"
            InputProps={{ readOnly: isFetchingRates }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          lg={2}>
          <LoadingButton
            fullWidth
            sx={{ minHeight: '40px' }}
            variant="contained"
            type="submit"
            disabled={invalid}
            loading={isFetchingCurrencies || isFetchingRates}
            startIcon={<SearchIcon />}
            loadingPosition="start">
            Search
          </LoadingButton>
        </Grid>
        {formErrorMsg && (
          <Grid
            item
            xs={12}>
            <Alert
              onClose={() => {
                setFormErrorMsg('');
              }}
              severity="error">
              {formErrorMsg}
            </Alert>
          </Grid>
        )}
      </Grid>
    </form>
  );
}

function LeftColumnHeader({ children }) {
  return (
    <TableCell
      component="th"
      scope="row"
      style={LP_LEFT_COLUMN_STYLE}>
      {children}
    </TableCell>
  );
}

function Actions({ permissions, updateLPRatesHandler }) {
  const isUpdatingLPRates = useSelector(selectIsUpdatingLPRates);
  const [showConfirmUpdateDialog, setShowConfirmUpdateDialog] = useState({
    show: false,
  });
  const { canUpdateLPRates } = permissions;

  if (!canUpdateLPRates) return null;

  return (
    <>
      <Typography
        className="form-header"
        variant="formHeader">
        Actions
      </Typography>
      <Box>
        <LoadingButton
          fullWidth={false}
          loading={isUpdatingLPRates}
          variant="contained"
          onClick={() => {
            setShowConfirmUpdateDialog({ show: true });
          }}
          startIcon={<ReplayCircleFilledIcon />}
          style={{ marginRight: 10 }}>
          Update Rates
        </LoadingButton>
      </Box>
      <ConfirmationDialog
        openDialog={showConfirmUpdateDialog}
        yesHandler={() => updateLPRatesHandler()}
        title="Update Loyalty Points Rates"
        message="Are you sure you want to update the rates?"
      />
    </>
  );
}

export default function LoyaltyPointsRates({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formValues = useRef({ currency: 'MYR', lob: null });

  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [displayRates, setDisplayRates] = useState([]);

  const isFetchingCurrencies = useSelector(selectIsFetchingLPRates);
  const isFetchingRates = useSelector(selectIsFetchingLPRates);
  const isFetchingLOBs = useSelector(selectIsFetchingLOBList);
  const currencies = useSelector(selectMCCCurrencies);
  const lpRates = useSelector(selectLoyaltyPointsRates);
  const lobs = useSelector(selectLOBList);

  useEffect(() => {
    if (!permissions?.canViewLPRates) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewLPRates));
    dispatch(getLOBs());
    dispatch(getMCCCurrencies());
    dispatch(loyaltyActions.resetLPRates());
  }, [dispatch]);

  useEffect(() => {
    if (!lpRates || !lpRates.baseConversionRates) {
      setDisplayRates([]);
      return;
    }

    const { baseConversionRates: baseRates } = lpRates;

    const rates = Object.entries(baseRates)
      .map(([currency, baseRate]) => ({
        currency,
        baseRate,
      }))
      .sort((a, b) => a.currency.localeCompare(b.currency));

    setDisplayRates(rates);
  }, [lpRates]);

  const fetchLoyaltyPointsRates = (params) => {
    setFormErrorMsg('');

    if (!params) return;

    dispatch(getLoyaltyPointsRates(params))
      .then(({ payload, error }) => {
        if (payload.baseConversionRates) return;

        if (error?.message) {
          setFormErrorMsg(JSON.stringify(error.message));
          return;
        }

        if (payload?.message) {
          setFormErrorMsg(JSON.stringify(payload.message));
          return;
        }

        setFormErrorMsg('No rates found');
      })
      .catch((error) => {
        setFormErrorMsg(error);
        return { [FORM_ERROR]: error };
      });
  };

  const onSubmit = (values) => {
    const { currency, lob } = values;

    if (!currency || !lob) return;

    const payload = { currency, lobName: lob.lobCode };

    fetchLoyaltyPointsRates(payload);
  };

  const validate = (values) => {
    if (!values.currency) return { [FORM_ERROR]: 'Currency is required' };
    if (!values.lob) return { [FORM_ERROR]: 'LOB is required' };
    return {};
  };

  const handleLOBChange = useCallback((value) => {
    formValues.current.lob = value;
  }, []);

  const updateLPRatesHandler = () => {
    dispatch(updateLPRates())
      .then(({ payload }) => {
        if (payload && payload.updateResult === 'SUCCESS') {
          dispatch(
            showSuccessMessage(
              `Loyalty rates updated successfully. Snapshot: ${payload.conversionSnapshotId}`,
            ),
          );
        } else {
          dispatch(
            showErrorMessage(
              payload.message
                ? payload.message
                : 'Failed to update loyalty rates',
            ),
          );
        }
      })
      .catch((error) => {
        dispatch(showErrorMessage(error || 'Failed to update loyalty rates'));
      });
  };

  const searchFormProps = {
    lobs,
    currencies: currencies && currencies.length ? currencies : ['MYR'],
    handleLOBChange,
    isFetchingCurrencies,
    isFetchingLOBs,
    isFetchingRates,
    formErrorMsg,
    setFormErrorMsg,
  };

  return (
    <ApplicationPageTemplate
      pageReady
      title="Loyalty Points Conversion Rates">
      <Form
        onSubmit={onSubmit}
        initialValues={formValues.current}
        validate={validate}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        render={({
          handleSubmit,
          values,
          invalid,
          pristine,
          form: {
            mutators: { setValue },
          },
        }) => (
          <SearchForm
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...searchFormProps}
            handleSubmit={handleSubmit}
            pristine={pristine}
            invalid={invalid}
            setValue={setValue}
            values={values}
          />
        )}
      />
      {displayRates.length > 0 && (
        <TableContainer sx={{ marginTop: '50px', maxWidth: '500px' }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <LeftColumnHeader>LOB</LeftColumnHeader>
                <TableCell>
                  {formValues.current && formValues.current.lob
                    ? formValues.current.lob.lobCode
                    : ''}
                </TableCell>
              </TableRow>
              <TableRow>
                <LeftColumnHeader>Currency</LeftColumnHeader>
                <TableCell>{lpRates?.currency}</TableCell>
              </TableRow>
              <TableRow>
                <LeftColumnHeader>Conversion Snapshot Id</LeftColumnHeader>
                <TableCell> {lpRates?.conversionSnapshotId}</TableCell>
              </TableRow>
              {lpRates && lpRates.conversionSnapshotId && (
                <TableRow>
                  <LeftColumnHeader>Conversion Timestamp</LeftColumnHeader>
                  <TableCell>
                    {dayjs(parseFloat(lpRates.conversionSnapshotId))
                      .tz('Asia/Kuala_Lumpur', true)
                      .format('DD/MM/YYYY hh:mm A zzz')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Table
            sx={{ marginTop: '20px' }}
            stickyHeader
            size="small">
            <TableHead>
              <TableRow>
                {LP_COLUMNS.map((column, index) => (
                  <TableCell
                    component="th"
                    key={column.id}
                    style={{
                      ...column.styles,
                      paddingLeft: index === 0 ? 0 : '16px',
                    }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRates.map((row) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.currency}>
                  {LP_COLUMNS.map((column, index) => {
                    const value = row[column.id];
                    return (
                      <TableCell
                        component="th"
                        key={column.id}
                        style={{
                          ...column.styles,
                          paddingLeft: index === 0 ? 0 : '16px',
                        }}
                        align={column.align}>
                        {column.format && typeof value === 'number'
                          ? column.format(value)
                          : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Actions
        permissions={permissions}
        updateLPRatesHandler={updateLPRatesHandler}
      />
    </ApplicationPageTemplate>
  );
}
